/**
 * Componente: Modal Informativo "¿Por qué te pedimos estos datos?" — PorQuePedimosDatosModal
 * Estilo Fiel a los Modales de AviFácil (PaisModalSelector, EstadoModalSelector, CiudadModalSelector, IdiomaModalSelector)
 */

'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './PorQuePedimosDatosModal.module.css';

export interface PorQuePedimosDatosModalProps {
  isOpen: boolean;
  onClose: () => void;
  paso?: number;
}

const ITEMS_INFO_PASO1 = [
  {
    icon: 'badge',
    title: 'Identificación Oficial del Predio',
    description:
      'Permite emitir reportes de lotes y guías de movilización válidos ante entidades sanitarias (ICA, SENASA, SAG).',
  },
  {
    icon: 'thermostat',
    title: 'Curvas de Manejo y Clima Bioseguro',
    description:
      'Adaptamos automáticamente las tablas zootécnicas de temperatura, humedad, altitud y ventilación a tu zona geográfica.',
  },
  {
    icon: 'lock',
    title: 'Privacidad y Cifrado Total',
    description:
      'Tu información productiva y comercial es 100% tuya y confidencial. No se comparte jamás con intermediarios ni competidores.',
  },
];

const ITEMS_INFO_PASO2 = [
  {
    icon: 'badge',
    title: 'Identidad Legal del Administrador',
    description:
      'Registramos tu nombre como productor titular responsable para la validez jurídica de tus guías, compras y despachos de aves.',
  },
  {
    icon: 'call',
    title: 'Canal Directo de Soporte por WhatsApp',
    description:
      'Elie y nuestro equipo zootécnico se comunicarán contigo para asistirte en emergencias sanitarias, pesajes y soporte 24/7.',
  },
  {
    icon: 'mail',
    title: 'Notificaciones Críticas y Seguridad',
    description:
      'Tu correo es la llave maestra para autorizar accesos, alertas tempranas de mortalidad y reportes automáticos de cierre de lote.',
  },
];

const ITEMS_INFO_PASO3 = [
  {
    icon: 'key',
    title: 'Llave Maestra y Control Administrativo',
    description:
      'Tu contraseña protege tu panel operativo, estadísticas de conversión alimenticia, inventarios de aves y finanzas.',
  },
  {
    icon: 'lock',
    title: 'Cifrado Criptográfico Irreversible',
    description:
      'Almacenamos tus credenciales con hashing de alta seguridad (Argon2 / AES-256). Nadie, ni siquiera nuestro equipo, puede ver tu clave.',
  },
  {
    icon: 'verified_user',
    title: 'Protección Jurídica y Propiedad de Datos',
    description:
      'Aceptar los términos garantiza que toda tu información productiva te pertenece al 100% y nunca será compartida con terceros.',
  },
];

export function PorQuePedimosDatosModal({
  isOpen,
  onClose,
  paso = 1,
}: PorQuePedimosDatosModalProps) {
  const [mounted, setMounted] = useState(false);
  const items = paso === 3 ? ITEMS_INFO_PASO3 : paso === 2 ? ITEMS_INFO_PASO2 : ITEMS_INFO_PASO1;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Cerrar con tecla Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="por-que-modal-title"
    >
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
        data-scrollable="true"
      >
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer focus:outline-none z-10"
          aria-label="Cerrar modal"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>

        {/* Encabezado: Icono Degradado y Título */}
        <div className="flex items-center gap-3 pr-10">
          <div className={styles.iconBadge}>
            <span className="material-symbols-outlined text-[24px] sm:text-[26px]">verified_user</span>
          </div>
          <div>
            <h2
              id="por-que-modal-title"
              className="text-lg sm:text-xl font-bold text-gray-900 leading-tight"
            >
              ¿Por qué te pedimos estos datos?
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Protección, bioseguridad y trazabilidad para tu granja
            </p>
          </div>
        </div>

        {/* Lista de Tarjetas Informativas */}
        <div className={styles.infoCardsContainer}>
          {items.map((item, index) => (
            <div key={index} className={styles.infoCard}>
              <div className={styles.itemIconCircle}>
                <span className="material-symbols-outlined text-[20px] sm:text-[22px]">
                  {item.icon}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-xs sm:text-sm font-bold text-gray-800 leading-snug">
                  {item.title}
                </h3>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Botón de Confirmación / Cerrar */}
        <button
          type="button"
          onClick={onClose}
          className={styles.understoodBtn}
        >
          <span>Entendido, continuar</span>
          <span className="material-symbols-outlined text-[18px]">check_circle</span>
        </button>

        {/* Nota de Confianza */}
        <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1.5 mt-2.5 select-none">
          <span className="material-symbols-outlined text-[13px] text-emerald-500">
            shield
          </span>
          <span>Tus datos están protegidos bajo protocolos de seguridad avícola</span>
        </p>
      </div>
    </div>,
    document.body
  );
}
